import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * CRM leads (PD-1993, docs/CRM.md). A lead is a person who confirmed a phone number with us —
 * at the patient portal's registration phone check — or whom staff entered by hand, and becomes
 * `registered` once a Patient is linked. Every lead carries one source: where it first came from,
 * kept for good.
 *
 * Any practitioner of the signed-in project; patients get FORBIDDEN. A lead is fenced to its
 * Medplum project.
 *
 * Browser-safe (oc + zod only): the value lists below are what the frontends import, and
 * src/crm/crm-shared.ts re-exports them for the Drizzle schema's CHECKs.
 */

/**
 * `new` for a lead staff entered by hand (New lead) that has neither confirmed a phone nor a
 * chart yet; `confirmed` once the phone is verified; `registered` once a Patient is linked. `scheduled`,
 * `canceled`, `completed` and `no-show` follow the patient's appointments, at the points that
 * send Brevo its lead status today. Staff can set any status by hand; `not-interested` is the
 * opt-out. Appointment updates never overwrite `not-interested`, and never move a lead off
 * `completed` (the final state); a campaign never reaches a `not-interested` lead.
 */
export const CRM_LEAD_STATUSES = [
  'new',
  'confirmed',
  'registered',
  'scheduled',
  'canceled',
  'completed',
  'no-show',
  'not-interested',
] as const

/**
 * Where a lead came from, derived from the attribution (src/crm/attribution.ts): an ad click,
 * `website` for a visit referred by our marketing site or another site (Website / Organic),
 * `zocdoc`, `manual` for staff-added and imported patients (Manual / Import), and `unknown` for
 * no tracking at all — never assumed to be organic.
 */
export const CRM_LEAD_SOURCES = [
  'google-ads',
  'facebook-ads',
  'website',
  'zocdoc',
  'manual',
  'unknown',
] as const

/** How the lead entered the CRM: `manual` is Add patient and New lead. */
export const CRM_CAPTURE_METHODS = ['registration', 'zocdoc', 'manual', 'import'] as const

/** How a campaign sends: by text, by email, or `both` (each member gets a text and an email). */
export const CRM_CAMPAIGN_CHANNELS = ['sms', 'email', 'both'] as const

/** The channel of one delivery: a `both` campaign gives each member one of each. */
export const CRM_DELIVERY_CHANNELS = ['sms', 'email'] as const

/** `draft` while its cohort can change; `launched` freezes the cohort and sends. */
export const CRM_CAMPAIGN_STATUSES = ['draft', 'launched'] as const

/**
 * One message to one cohort member on one channel, created at launch. `skipped` never sends
 * (see the skip reasons); `sending` is claimed and in flight — a run that died there is never
 * retried, so nobody gets the same message twice.
 */
export const CRM_DELIVERY_STATUSES = ['pending', 'sending', 'sent', 'failed', 'skipped'] as const

/**
 * Why a delivery was skipped: the lead is `not-interested` (at launch), has no email (an email,
 * at launch), or no phone we can text (an SMS, when its turn to send comes).
 */
export const CRM_SKIP_REASONS = ['not-interested', 'no-email', 'no-phone'] as const

export type CrmLeadStatus = (typeof CRM_LEAD_STATUSES)[number]
export type CrmLeadSource = (typeof CRM_LEAD_SOURCES)[number]
export type CrmCaptureMethod = (typeof CRM_CAPTURE_METHODS)[number]
export type CrmCampaignChannel = (typeof CRM_CAMPAIGN_CHANNELS)[number]
export type CrmDeliveryChannel = (typeof CRM_DELIVERY_CHANNELS)[number]
export type CrmCampaignStatus = (typeof CRM_CAMPAIGN_STATUSES)[number]
export type CrmDeliveryStatus = (typeof CRM_DELIVERY_STATUSES)[number]
export type CrmSkipReason = (typeof CRM_SKIP_REASONS)[number]

/** One visit's tracking, as the browser captured it. Every field is optional. */
export const crmAttributionSchema = z.object({
  utmSource: z.string().max(500).optional(),
  utmMedium: z.string().max(500).optional(),
  utmCampaign: z.string().max(500).optional(),
  utmContent: z.string().max(500).optional(),
  utmTerm: z.string().max(500).optional(),
  /** Google Ads click id. */
  gclid: z.string().max(500).optional(),
  /** Facebook click id. */
  fbclid: z.string().max(500).optional(),
  /** Google's auto-tagged campaign id (gad_campaignid). */
  gadCampaignId: z.string().max(500).optional(),
  landingPage: z.string().max(2000).optional(),
  referrer: z.string().max(2000).optional(),
  /** When the browser saw the visit. */
  capturedAt: z.string().datetime({ offset: true }).optional(),
})

export type CrmAttribution = z.infer<typeof crmAttributionSchema>

/** Mirrors ModelPageRequest (src/model/types.ts). */
export const pageRequestSchema = z.object({
  offset: z.number().int().min(0),
  count: z.number().int().min(0).max(200),
  limit: z.number().int().min(0),
})

const touchSchema = z.object({
  source: z.enum(CRM_LEAD_SOURCES),
  /** utm_campaign; null when the visit wasn't tagged. */
  campaign: z.string().nullable(),
  attribution: crmAttributionSchema.nullable(),
})

export const pageResponseSchema = pageRequestSchema.extend({
  size: z.number().int().min(0),
  total: z.number().int().min(0),
})

/** FHIR AdministrativeGender, as the chart stores it. */
export const CRM_GENDERS = ['male', 'female', 'other', 'unknown'] as const
export type CrmGender = (typeof CRM_GENDERS)[number]

/**
 * A lead's registration details: what staff enter for a new lead, and, for a lead with a chart,
 * what the chart says (copied each time the CRM hears from it). Null when unknown.
 */
const detailsShape = {
  /** YYYY-MM-DD. */
  birthDate: z.string().nullable(),
  gender: z.enum(CRM_GENDERS).nullable(),
  /** Street, apartment and city, one line. */
  address: z.string().nullable(),
  postalCode: z.string().nullable(),
  /** Two-letter US state code. */
  state: z.string().nullable(),
}

export const leadSchema = z.object({
  id: z.number().int(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  phone: z.string(),
  email: z.string().nullable(),
  ...detailsShape,
  status: z.enum(CRM_LEAD_STATUSES),
  captureMethod: z.enum(CRM_CAPTURE_METHODS),
  /** The linked Patient's id once registered. */
  patientId: z.string().nullable(),
  /** Where the lead first came from. */
  original: touchSchema,
  /** Free-text notes staff keep on the lead; null when there are none. */
  notes: z.string().nullable(),
  /** Null for a lead that never confirmed a phone with us (Zocdoc, import, manual). */
  confirmedAt: z.string().nullable(),
  registeredAt: z.string().nullable(),
  /** When the lead entered the CRM. */
  createdAt: z.string(),
  updatedAt: z.string(),
})

/**
 * How far a lead got, as the report counts it: `registered` has a linked Patient; `booked` is
 * scheduled or has completed an appointment ("Booked / Seen").
 */
export const CRM_LEAD_MILESTONES = ['registered', 'booked'] as const
export type CrmLeadMilestone = (typeof CRM_LEAD_MILESTONES)[number]

/** The list's filters; also how a campaign's cohort is filled from the list. */
export const leadFilterSchema = z.object({
  /** Matches name, phone or email. */
  search: z.string().trim().max(200).optional(),
  status: z.enum(CRM_LEAD_STATUSES).optional(),
  /** The original source. */
  source: z.enum(CRM_LEAD_SOURCES).optional(),
  /** Two-letter US state code. */
  state: z.string().trim().length(2).toUpperCase().optional(),
  /** A ZIP, or the start of one: "100" finds every ZIP that starts with 100. */
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{1,5}$/)
    .optional(),
  /** The original utm_campaign; null for leads whose first visit had none. */
  campaign: z.string().max(500).nullable().optional(),
  milestone: z.enum(CRM_LEAD_MILESTONES).optional(),
  /**
   * Leads this SMS/email campaign reached (a message was sent); `milestone` then counts only
   * the leads that got there after the launch.
   */
  outreach: z.number().int().positive().optional(),
})

export type CrmLeadFilter = z.infer<typeof leadFilterSchema>

export const crmLeadList = oc
  .route({ method: 'POST', path: '/v2/crm/leads/list' })
  .input(
    z.object({
      ...leadFilterSchema.shape,
      page: pageRequestSchema,
    }),
  )
  .output(
    z.object({
      page: pageResponseSchema,
      /** Newest first. */
      data: z.array(leadSchema),
    }),
  )

const funnelSchema = z.object({
  leads: z.number().int().min(0),
  registered: z.number().int().min(0),
  booked: z.number().int().min(0),
})

/**
 * How leads turn into patients (see CRM_LEAD_MILESTONES), three ways, each count a number of
 * distinct leads that the list's filters return exactly:
 * - `sources`: every lead by original source (`source` + `milestone`).
 * - `adCampaigns`: each tagged ad campaign and the leads it brought in (`source` + `campaign`).
 *   Most leads first.
 * - `outreach`: each launched SMS/email campaign, which only ever reaches leads already in the
 *   CRM: how many it reached, how many of those could still register or book (`registerable`,
 *   `bookable`: not there at launch), and how many did after the launch (`outreach` +
 *   `milestone`). Latest launch first.
 */
export const crmLeadReport = oc
  .route({ method: 'POST', path: '/v2/crm/leads/report' })
  .input(z.object({}))
  .output(
    z.object({
      sources: z.array(funnelSchema.extend({ source: z.enum(CRM_LEAD_SOURCES) })),
      adCampaigns: z.array(
        funnelSchema.extend({ source: z.enum(CRM_LEAD_SOURCES), campaign: z.string() }),
      ),
      outreach: z.array(
        z.object({
          campaignId: z.number().int(),
          name: z.string(),
          channel: z.enum(CRM_CAMPAIGN_CHANNELS),
          launchedAt: z.string().datetime().nullable(),
          reached: z.number().int().min(0),
          registerable: z.number().int().min(0),
          registered: z.number().int().min(0),
          bookable: z.number().int().min(0),
          booked: z.number().int().min(0),
        }),
      ),
    }),
  )

const statusCountsSchema = z.array(
  z.object({ status: z.enum(CRM_LEAD_STATUSES), count: z.number().int().min(0) }),
)

/**
 * One report campaign's leads by status, every status listed in order (zero included): an ad
 * campaign's leads (`source` + `campaign`), or the leads an SMS/email campaign reached
 * (`outreach`), with — for the latter — the same leads' statuses when it launched.
 */
export const crmLeadStatusBreakdown = oc
  .route({ method: 'POST', path: '/v2/crm/leads/status-breakdown' })
  .input(leadFilterSchema.pick({ source: true, campaign: true, outreach: true }))
  .output(z.object({ now: statusCountsSchema, atLaunch: statusCountsSchema.nullable() }))

/**
 * Staff enter a lead by hand (New lead) — typically a Zocdoc patient — as a `new` lead with no
 * chart, capture method `manual`. The phone is normalized to E.164 and must be a US mobile number
 * not already a lead: CONFLICT otherwise. The lead links to a chart when that phone signs up or is
 * added as a patient.
 */
/** A YYYY-MM-DD that names a real calendar day, not in the future (UTC, so "today" passes). */
const isPastCalendarDate = (value: string) => {
  const date = new Date(`${value}T00:00:00Z`)
  return (
    !Number.isNaN(date.getTime()) &&
    date.toISOString().slice(0, 10) === value &&
    date.getTime() <= Date.now()
  )
}

/** A name, as the Patient App takes it: letters, spaces, apostrophes and hyphens (PD-2099). */
export const CRM_NAME_PATTERN = /^[a-zA-Z\s'-]+$/
/** A 5-digit ZIP or ZIP+4, written as the Patient App writes it: 10001 or 10001-1234. */
export const CRM_POSTAL_CODE_PATTERN = /^\d{5}(-\d{4})?$/

const leadName = z.string().trim().min(2).max(200).regex(CRM_NAME_PATTERN)

export const leadCreateInputSchema = z.object({
  firstName: leadName,
  lastName: leadName,
  phone: z.string().trim().min(1).max(40),
  email: z.string().trim().email().max(320).optional(),
  birthDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine(isPastCalendarDate, 'Enter a real date of birth')
    .optional(),
  gender: z.enum(CRM_GENDERS).optional(),
  address: z.string().trim().max(500).optional(),
  postalCode: z.string().trim().regex(CRM_POSTAL_CODE_PATTERN).optional(),
  state: z.string().trim().length(2).toUpperCase().optional(),
  source: z.enum(CRM_LEAD_SOURCES),
  /** utm_campaign-style name, optional. */
  campaign: z.string().trim().min(1).max(500).optional(),
})

export const crmLeadCreate = oc
  .route({ method: 'POST', path: '/v2/crm/leads/create' })
  .input(leadCreateInputSchema)
  .output(leadSchema)

/** Staff set a lead's status by hand — typically not-interested after an opt-out. */
export const crmLeadSetStatus = oc
  .route({ method: 'POST', path: '/v2/crm/leads/set-status' })
  .input(z.object({ leadId: z.number().int(), status: z.enum(CRM_LEAD_STATUSES) }))
  .output(leadSchema)

/** Staff replace a lead's notes; an empty string clears them. */
export const crmLeadSetNotes = oc
  .route({ method: 'POST', path: '/v2/crm/leads/set-notes' })
  .input(z.object({ leadId: z.number().int(), notes: z.string().max(5000) }))
  .output(leadSchema)

/**
 * A patient staff just created with Add patient becomes a `registered` lead (capture method and
 * source `manual`). Any practitioner — not only CRM users — since everyone can add patients. A
 * patient whose phone is already a lead is linked to it, keeping that lead's source. A patient
 * with no textable phone gets no lead.
 */
export const crmLeadRecordManual = oc
  .route({ method: 'POST', path: '/v2/crm/leads/record-manual' })
  .input(z.object({ patientId: z.string().min(1) }))
  .output(z.object({ recorded: z.boolean() }))

/**
 * Adds the project's existing patients as `registered` leads (capture method `import`, source
 * `manual`, shown as Manual / Import). Safe to run again: a patient whose phone or chart is already a lead is left alone.
 * A patient with no textable phone is counted and skipped.
 */
export const crmLeadImportPatients = oc
  .route({ method: 'POST', path: '/v2/crm/leads/import-patients' })
  .input(z.object({}))
  .output(
    z.object({
      /** Patients read. */
      scanned: z.number().int().min(0),
      added: z.number().int().min(0),
      /** No phone we could text, so no lead. */
      skippedNoPhone: z.number().int().min(0),
    }),
  )
